import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { createTheme, ThemeProvider } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Paper from '@material-ui/core/Paper'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import MenuItem from '@material-ui/core/MenuItem'
import MuiSelect from '@material-ui/core/Select'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import CircularProgress from '@material-ui/core/CircularProgress'

const brandTheme = createTheme({
  palette: {
    primary: {
      main: '#008fcf',
      dark: '#007ab3',
    },
  },
})
import AsyncSelect from 'react-select/async'
import Select from 'react-select'
import District from '../../models/District'
import School from '../../models/School'
import KonvaCanvas from '../NewTemplateEditor/KonvaCanvas/KonvaCanvas'
import { createTextField } from '../NewTemplateEditor/KonvaCanvas/canvasUtils'
import SpellCheckPanel from './SpellCheckPanel'
import styles from './CustomerTemplateEditor.module.scss'

/**
 * CustomerTemplateEditor - A component for customers to preview and fill in template-driven products
 *
 * Props:
 * - template: The template object with dimensions and settings
 * - fields: Array of field objects from the template
 * - imageUrl: URL of the template background image
 * - itemId: The item ID for adding to cart
 * - cartId: The cart ID
 * - submitUrl: The URL to POST form data to
 * - itemName: Name of the item
 * - itemNumber: Item number (e.g., "1178c")
 * - itemDescription: Description of the item (optional)
 * - quantityOptions: Array of quantity options [{name, label, default}]
 * - previewImageUrl: URL of the item preview image (optional)
 * - hasSchoolInfo: Whether school info has already been collected
 * - schoolId: Current school ID (if set)
 * - schoolName: Current school name (if set)
 * - schoolEmployee: Current school employee position (if set)
 * - saveSchoolInfoUrl: URL to POST school info to
 */
export default function CustomerTemplateEditor({
  template,
  fields: fieldsArray,
  imageUrl,
  itemId,
  cartId,
  submitUrl,
  itemName,
  itemNumber,
  itemDescription,
  quantityOptions = [{ name: 'quantity', label: 'Quantity', default: 0 }],
  previewImageUrl,
  // School info props
  hasSchoolInfo = false,
  schoolId = null,
  schoolName = null,
  schoolEmployee = null,
  saveSchoolInfoUrl = null,
  // Proof generation
  generateProofUrl = null,
  // Fonts for the toolbar
  fonts = [],
  // Draft persistence
  draftSaveUrl = null,
  initialDraft = {},
  // Full field state for restoration (includes positions, custom fields, etc.)
  initialFieldState = null,
  // Multipage support
  pages: initialPages = [],
}) {
  // Step management: 'school_info', 'template_edit', or 'proof_review'
  const [currentStep, setCurrentStep] = useState(hasSchoolInfo ? 'template_edit' : 'school_info')

  // Proof generation state
  const [isGeneratingProof, setIsGeneratingProof] = useState(false)
  const [proofPdfUrl, setProofPdfUrl] = useState(null)
  const [proofPageImages, setProofPageImages] = useState([])
  const [proofCurrentPage, setProofCurrentPage] = useState(0)
  const [hasViewedProofFront, setHasViewedProofFront] = useState(false)
  const [hasViewedProofBack, setHasViewedProofBack] = useState(false)
  const [proofError, setProofError] = useState(null)
  const [proofZoom, setProofZoom] = useState(100)

  // School selection state
  const [selectedDistrict, setSelectedDistrict] = useState(null)
  const [selectedSchool, setSelectedSchool] = useState(
    schoolId && schoolName ? { value: schoolId, label: schoolName } : null
  )
  const [selectedPosition, setSelectedPosition] = useState(
    schoolEmployee ? { value: schoolEmployee, label: schoolEmployee } : null
  )
  const [isSavingSchool, setIsSavingSchool] = useState(false)
  const [schoolError, setSchoolError] = useState(null)

  // Current school name for template fields
  const [currentSchoolName, setCurrentSchoolName] = useState(schoolName || '')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errors, setErrors] = useState({})
  const [approved, setApproved] = useState(false)
  const [checkoutUrl, setCheckoutUrl] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')
  const [hideFrameEdges, setHideFrameEdges] = useState(false)
  const [showSafetyZone, setShowSafetyZone] = useState(true)
  const [showSpellCheck, setShowSpellCheck] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const canvasExportFnRef = useRef(null)
  const initialCanvasFieldsRef = useRef(null)

  // Multipage support: template background = Page 1, TemplatePages = Page 2+
  const [currentPage, setCurrentPage] = useState(0)
  const [hasViewedBack, setHasViewedBack] = useState(false)
  const isMultipage = initialPages.length > 0
  const allPages = useMemo(() => {
    if (!isMultipage) return []
    return [
      { id: null, position: 1, imageUrl: imageUrl },
      ...initialPages.map((p, idx) => ({ ...p, position: idx + 2 }))
    ]
  }, [initialPages, imageUrl, isMultipage])
  const currentPageData = isMultipage ? allPages[currentPage] : null
  const currentPageId = currentPageData ? currentPageData.id : null
  const currentImageUrl = isMultipage ? (currentPageData?.imageUrl || imageUrl) : imageUrl

  // Helper to check if a field is a School Name field
  const isSchoolNameField = (field, columnName, original = null) => {
    const isSchoolNameColumn = columnName &&
      columnName.toLowerCase().includes('school') &&
      columnName.toLowerCase().includes('name')
    const hasSchoolNamePlaceholder =
      field.text === 'School Name' ||
      field.sampleText === 'School Name' ||
      field.sample_text === 'School Name' ||
      original?.text === 'School Name' ||
      original?.sample_text === 'School Name' ||
      original?.sampleText === 'School Name'
    return isSchoolNameColumn || hasSchoolNamePlaceholder
  }

  // Convert fields array to object format for KonvaCanvas (keyed by ID)
  // This allows local editing without saving to the database
  // If initialFieldState is provided, use it to restore complete field state (including custom fields)
  // Otherwise, build from fieldsArray and restore text values from initialDraft
  const [canvasFields, setCanvasFields] = useState(() => {
    // If we have saved field state, restore it completely (includes custom fields)
    if (initialFieldState && Array.isArray(initialFieldState) && initialFieldState.length > 0) {
      const fieldsObj = {}
      initialFieldState.forEach(field => {
        // Check if this is a School Name field that should show actual school name
        const shouldUseSchoolName = schoolName && isSchoolNameField(field, field.label)

        fieldsObj[field.id] = {
          id: field.id,
          left: field.left,
          top: field.top,
          width: field.width,
          height: field.height,
          textSize: field.textSize,
          color: field.color,
          align: field.align,
          verticalAlign: field.verticalAlign || 'middle',
          spacing: field.spacing,
          rotation: field.rotation || 0,
          strokeColor: field.strokeColor,
          strokeWidth: field.strokeWidth || 0,
          text: shouldUseSchoolName ? schoolName : field.text,
          sampleText: shouldUseSchoolName ? null : field.sampleText,
          wrap: field.wrap,
          fontName: field.fontName,
          fontId: field.fontId,
          fontStyle: field.fontStyle || 'normal',
          textDecoration: field.textDecoration || '',
          autoFit: field.autoFit,
          minFontSize: field.minFontSize,
          label: field.label,
          columnType: field.columnType,
          isCustomField: field.isCustomField || false,
          templatePageId: field.templatePageId || null,
        }
      })
      return fieldsObj
    }

    // Otherwise, build from template fields array
    const fieldsObj = {}
    fieldsArray.forEach(field => {
      const columnName = field.name || field.column_name
      const draftValue = columnName && initialDraft ? initialDraft[columnName] : null

      // Check if this is a School Name field that should show actual school name
      const shouldUseSchoolName = schoolName && isSchoolNameField(field, columnName)

      fieldsObj[field.id] = {
        id: field.id,
        left: field.x,
        top: field.y,
        width: field.width,
        height: field.height,
        textSize: field.text_size || field.textSize,
        color: field.color,
        align: field.align,
        verticalAlign: field.vertical_align || field.verticalAlign || 'middle',
        spacing: field.spacing,
        rotation: field.rotation || 0,
        strokeColor: field.strokeColor || field.stroke_color,
        strokeWidth: field.strokeWidth || field.stroke_width || 0,
        // Use school name for School Name fields, otherwise draft value or original text
        text: shouldUseSchoolName ? schoolName : (draftValue || field.text),
        sampleText: shouldUseSchoolName ? null : (draftValue ? null : (field.sample_text || field.sampleText)),
        wrap: field.wrap,
        fontName: field.fontName || field.font?.name,
        fontId: field.fontId || field.font?.id,
        fontStyle: field.fontStyle || field.font_style || 'normal',
        textDecoration: field.textDecoration || field.text_decoration || '',
        autoFit: field.auto_fit || field.autoFit,
        minFontSize: field.minFontSize || field.min_font_size,
        label: field.field_label || field.fieldLabel || field.name || field.column_name,
        columnType: field.column_type || field.columnType,
        customerEditable: field.customer_editable || field.customerEditable,
        templatePageId: field.template_page_id || field.templatePageId || null,
        // Don't include 'object' - customers don't save to database
      }
    })
    return fieldsObj
  })

  // Track dirty state: capture initial snapshot and compare on changes
  useEffect(() => {
    if (!initialCanvasFieldsRef.current && Object.keys(canvasFields).length > 0) {
      initialCanvasFieldsRef.current = JSON.stringify(canvasFields)
    }
  }, [canvasFields])

  useEffect(() => {
    if (!initialCanvasFieldsRef.current) return
    const current = JSON.stringify(canvasFields)
    setIsDirty(current !== initialCanvasFieldsRef.current)
  }, [canvasFields])

  // Warn on browser navigation (refresh, back) with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  // Intercept the modal X close button when dirty
  useEffect(() => {
    const handler = (e) => {
      const closeBtn = e.target.closest('.custom-modal-close-button')
      if (closeBtn && isDirty) {
        if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
          e.stopPropagation()
          e.preventDefault()
        }
      }
    }
    document.addEventListener('click', handler, true) // capture phase
    return () => document.removeEventListener('click', handler, true)
  }, [isDirty])

  // Filter fields for current page (multipage) or show all (single-page)
  const visibleCanvasFields = useMemo(() => {
    if (!isMultipage) return canvasFields
    const filtered = {}
    Object.entries(canvasFields).forEach(([id, field]) => {
      const belongsToCurrentPage = field.templatePageId === currentPageId ||
        (currentPage === 0 && !field.templatePageId)
      if (belongsToCurrentPage) {
        filtered[id] = field
      }
    })
    return filtered
  }, [canvasFields, currentPageId, currentPage, isMultipage])

  // Refs for auto-save
  const saveTimeoutRef = useRef(null)
  const lastSavedRef = useRef(JSON.stringify(initialDraft || {}))

  const positionOptions = [
    { value: 'Principal', label: 'Principal' },
    { value: 'Vice-Principal', label: 'Vice-Principal' },
    { value: 'Office Manager', label: 'Office Manager' },
    { value: 'Secretary', label: 'Secretary' },
    { value: 'Teacher', label: 'Teacher' },
    { value: 'Educational Support', label: 'Educational Support' },
  ]

  // Build a map of field ID to original field data (for column names)
  const fieldIdToOriginal = useMemo(() => {
    const map = {}
    fieldsArray.forEach(field => {
      map[field.id] = field
    })
    return map
  }, [fieldsArray])

  // Derive template data from canvasFields for form submission
  // This converts from field ID-keyed object to column_name-keyed object
  const getTemplateDataFromCanvas = useCallback(() => {
    const templateData = {}
    Object.values(canvasFields).forEach(field => {
      const original = fieldIdToOriginal[field.id]
      const columnName = original?.name || original?.column_name || original?.columnName
      if (columnName) {
        // Use text if set, otherwise fall back to sampleText
        const textValue = field.text != null && field.text !== '' ? field.text : (field.sampleText || '')
        templateData[columnName] = textValue
      }
    })
    return templateData
  }, [canvasFields, fieldIdToOriginal])

  // Get full field state for persistence (includes positions, styles, custom fields)
  // This allows customers to return and see their exact layout
  const getFullFieldState = useCallback(() => {
    return Object.values(canvasFields).map(field => ({
      id: field.id,
      left: field.left,
      top: field.top,
      width: field.width,
      height: field.height,
      textSize: field.textSize,
      color: field.color,
      align: field.align,
      verticalAlign: field.verticalAlign,
      spacing: field.spacing,
      rotation: field.rotation || 0,
      strokeColor: field.strokeColor,
      strokeWidth: field.strokeWidth || 0,
      text: field.text,
      sampleText: field.sampleText,
      wrap: field.wrap,
      fontName: field.fontName,
      fontId: field.fontId,
      fontStyle: field.fontStyle,
      textDecoration: field.textDecoration,
      autoFit: field.autoFit,
      minFontSize: field.minFontSize,
      label: field.label,
      columnType: field.columnType,
      isCustomField: field.isCustomField || false,
      templatePageId: field.templatePageId || null,
    }))
  }, [canvasFields])

  // Auto-save drafts when canvas fields change
  // TODO: Re-enable after fixing draft data structure
  // Currently disabled because it was saving corrupted data
  /*
  useEffect(() => {
    if (!draftSaveUrl) return

    const templateData = getTemplateDataFromCanvas()
    const currentDataStr = JSON.stringify(templateData)

    // Skip if data hasn't changed
    if (currentDataStr === lastSavedRef.current) return

    // Debounce: clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Save after 1.5 seconds of no changes
    saveTimeoutRef.current = setTimeout(() => {
      fetch(draftSaveUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ template_data: templateData })
      })
        .then(response => {
          if (response.ok) {
            lastSavedRef.current = currentDataStr
            console.log('Draft saved')
          }
        })
        .catch(error => console.error('Error saving draft:', error))
    }, 1500)

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [canvasFields, draftSaveUrl, getTemplateDataFromCanvas])
  */

  // Update school name in canvasFields when it changes
  useEffect(() => {
    if (currentSchoolName) {
      setCanvasFields(prev => {
        const updated = { ...prev }
        Object.keys(updated).forEach(fieldId => {
          const field = updated[fieldId]
          const original = fieldIdToOriginal[fieldId]
          const columnName = original?.name || original?.column_name || original?.columnName

          if (isSchoolNameField(field, columnName, original)) {
            // Update the field with the actual school name
            updated[fieldId] = { ...updated[fieldId], text: currentSchoolName, sampleText: null }
          }
        })
        return updated
      })
    }
  }, [currentSchoolName, fieldIdToOriginal])

  // Initialize quantities
  const [quantities, setQuantities] = useState(() => {
    const initial = {}
    quantityOptions.forEach(opt => {
      initial[opt.name] = 0
    })
    return initial
  })

  // District search
  const loadDistrictOptions = (inputValue, callback) => {
    District
      .where({ name: { match: inputValue } })
      .order({ name: 'asc' })
      .per(50)
      .all()
      .then((res) => {
        const options = res.data.map((e) => ({ label: e.name, value: e.id, data: e }))
        callback(options)
      })
      .catch(() => callback([]))
  }

  // School search (filtered by district)
  const loadSchoolOptions = (inputValue, callback) => {
    if (!selectedDistrict) {
      callback([])
      return
    }
    School
      .where({
        district_id: selectedDistrict.data.id,
        name: { match: inputValue },
      })
      .order({ name: 'asc' })
      .per(50)
      .all()
      .then((res) => {
        const options = res.data.map((e) => ({ label: e.name, value: e.id, data: e }))
        callback(options)
      })
      .catch(() => callback([]))
  }

  const handleDistrictChange = (option) => {
    setSelectedDistrict(option)
    setSelectedSchool(null) // Reset school when district changes
    setSchoolError(null)
  }

  const handleSchoolChange = (option) => {
    setSelectedSchool(option)
    setSchoolError(null)
  }

  const handlePositionChange = (option) => {
    setSelectedPosition(option)
    setSchoolError(null)
  }

  const handleSaveSchoolInfo = async () => {
    if (!selectedSchool || !selectedPosition) {
      setSchoolError('Please select your school and position')
      return
    }

    setIsSavingSchool(true)
    setSchoolError(null)

    try {
      const response = await fetch(saveSchoolInfoUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          school_id: selectedSchool.value,
          school_employee: selectedPosition.value,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        setCurrentSchoolName(data.school_name || selectedSchool.label)
        setCurrentStep('template_edit')
      } else {
        const errorData = await response.json().catch(() => ({}))
        setSchoolError(errorData.error || 'Failed to save school information. Please try again.')
      }
    } catch (error) {
      console.error('Error saving school info:', error)
      setSchoolError('An error occurred. Please try again.')
    } finally {
      setIsSavingSchool(false)
    }
  }

  // Generate proof PDF
  const handleGenerateProof = async () => {
    if (!generateProofUrl) {
      setProofError('Proof generation is not available')
      return
    }

    setIsGeneratingProof(true)
    setProofError(null)
    setProofPdfUrl(null)

    try {
      // Derive template data from the canvas fields
      const templateData = getTemplateDataFromCanvas()
      // Get full field state for accurate PDF rendering
      const fieldState = getFullFieldState()

      const response = await fetch(generateProofUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          template_data: templateData,
          field_state: fieldState,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        setProofPdfUrl(data.pdf_url)
        // Open PDF in new tab
        window.open(data.pdf_url, '_blank')
      } else {
        const errorData = await response.json().catch(() => ({}))
        setProofError(errorData.error || 'Failed to generate proof. Please try again.')
      }
    } catch (error) {
      console.error('Error generating proof:', error)
      setProofError('An error occurred. Please try again.')
    } finally {
      setIsGeneratingProof(false)
    }
  }

  const handleQuantityChange = useCallback((name, value) => {
    const numValue = Math.max(0, parseInt(value) || 0)
    setQuantities(prev => ({
      ...prev,
      [name]: numValue
    }))
    if (numValue >= 1) {
      setErrors(prev => {
        const { quantity, ...rest } = prev
        return rest
      })
    }
  }, [])

  const handleAddText = useCallback(() => {
    var newField = createTextField('New Text', 150, 150, {
      width: 200,
      height: 40,
      textSize: 20,
      align: 'center',
      wrap: true,
      templatePageId: currentPageId,
    })
    setCanvasFields(prev => ({ ...prev, [newField.id]: newField }))
  }, [currentPageId])

  const validateForm = () => {
    const newErrors = {}
    const templateData = getTemplateDataFromCanvas()

    // Validate required fields
    fieldsArray.forEach(field => {
      const isRequired = field.field_required || field.fieldRequired
      const columnName = field.name || field.column_name || field.columnName
      if (isRequired && columnName && !templateData[columnName]?.trim()) {
        newErrors[columnName] = 'This field is required'
      }
    })

    // Validate approval checkbox
    if (!approved) {
      newErrors['approval'] = 'You must approve the information before adding to cart'
    }

    // Validate at least one quantity > 0
    const totalQuantity = Object.values(quantities).reduce((sum, q) => sum + q, 0)
    if (totalQuantity === 0) {
      newErrors['quantity'] = quantityOptions.length > 1 ? 'Please enter a quantity for at least one option.' : 'Please enter a quantity.'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      // Derive template data from the canvas fields (column_name -> text value)
      const templateData = getTemplateDataFromCanvas()
      // Get full field state for PDF generation and restoration
      const fieldState = getFullFieldState()

      // Build form data for submission
      const submitData = {
        cart_items: {
          item_id: itemId,
          template_data: templateData,
          field_state: fieldState,
          ...quantities
        }
      }

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify(submitData),
      })

      if (response.ok) {
        const data = await response.json()
        setIsDirty(false)
        setIsSubmitted(true)
        setSuccessMessage(data.message || 'Item added to cart!')
        setCheckoutUrl(data.checkout_url)

        // Update cart quantity in header if element exists
        const cartQuantityEl = document.getElementById('cart_quantity')
        if (cartQuantityEl && data.cart_items_count) {
          cartQuantityEl.textContent = data.cart_items_count
        }
      } else {
        const errorData = await response.json().catch(() => ({}))
        alert(errorData.error || 'Failed to add to cart. Please try again.')
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      alert('An error occurred. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleContinueShopping = () => {
    // Close the modal by clicking the close button
    const closeBtn = document.querySelector('.custom-modal-close-button')
    if (closeBtn) {
      closeBtn.click()
    }
  }

  // Render school selection step
  const renderSchoolSelection = () => (
    <Box className={styles.schoolSelectionContainer}>
      <Typography variant="h4" style={{ color: '#008fcf', marginBottom: '8px', fontSize: '28px' }}>
        {itemName}
      </Typography>
      {itemNumber && (
        <Typography variant="body1" color="textSecondary" style={{ marginBottom: '24px', fontSize: '16px' }}>
          Item #{itemNumber}
        </Typography>
      )}

      <Paper elevation={1} style={{ padding: '32px', maxWidth: '600px', margin: '0 auto' }}>
        <Typography variant="h4" style={{ marginBottom: '16px', textAlign: 'center', fontWeight: 500, fontSize: '26px' }}>
          Please identify your school
        </Typography>
        <Typography variant="body1" color="textSecondary" style={{ marginBottom: '28px', textAlign: 'center', fontSize: '18px' }}>
          We need this information to customize your product.
        </Typography>

        <Box mb={3}>
          <Typography variant="body1" style={{ marginBottom: '10px', fontWeight: 600, fontSize: '18px' }}>
            School District
          </Typography>
          <AsyncSelect
            cacheOptions
            defaultOptions
            placeholder="Start typing to search districts..."
            value={selectedDistrict}
            loadOptions={loadDistrictOptions}
            onChange={handleDistrictChange}
            styles={{
              control: (base) => ({ ...base, minHeight: '48px', fontSize: '16px' }),
              option: (base) => ({ ...base, fontSize: '16px', padding: '12px 16px' }),
              placeholder: (base) => ({ ...base, fontSize: '16px' }),
              singleValue: (base) => ({ ...base, fontSize: '16px' }),
              input: (base) => ({ ...base, fontSize: '16px' }),
            }}
          />
        </Box>

        {selectedDistrict && (
          <Box mb={3}>
            <Typography variant="body1" style={{ marginBottom: '10px', fontWeight: 600, fontSize: '18px' }}>
              School
            </Typography>
            <AsyncSelect
              key={selectedDistrict.value}
              cacheOptions
              defaultOptions
              placeholder="Start typing to search schools..."
              value={selectedSchool}
              loadOptions={loadSchoolOptions}
              onChange={handleSchoolChange}
              styles={{
                control: (base) => ({ ...base, minHeight: '48px', fontSize: '16px' }),
                option: (base) => ({ ...base, fontSize: '16px', padding: '12px 16px' }),
                placeholder: (base) => ({ ...base, fontSize: '16px' }),
                singleValue: (base) => ({ ...base, fontSize: '16px' }),
                input: (base) => ({ ...base, fontSize: '16px' }),
              }}
            />
          </Box>
        )}

        {selectedSchool && (
          <Box mb={3}>
            <Typography variant="body1" style={{ marginBottom: '10px', fontWeight: 600, fontSize: '18px' }}>
              Your Position
            </Typography>
            <Select
              options={positionOptions}
              value={selectedPosition}
              onChange={handlePositionChange}
              placeholder="Select your position..."
              styles={{
                control: (base) => ({ ...base, minHeight: '48px', fontSize: '16px' }),
                option: (base) => ({ ...base, fontSize: '16px', padding: '12px 16px' }),
                placeholder: (base) => ({ ...base, fontSize: '16px' }),
                singleValue: (base) => ({ ...base, fontSize: '16px' }),
              }}
            />
          </Box>
        )}

        {schoolError && (
          <Typography variant="body1" color="error" style={{ marginBottom: '16px', textAlign: 'center', fontSize: '16px' }}>
            {schoolError}
          </Typography>
        )}

        {selectedSchool && selectedPosition && (
          <Box mt={4} pt={3} style={{ borderTop: '1px solid #eee', textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSaveSchoolInfo}
              disabled={isSavingSchool}
              style={{ minWidth: '220px', fontSize: '18px', padding: '14px 32px' }}
            >
              {isSavingSchool ? 'Saving...' : 'Continue'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  )

  // Handle cancel - close the modal and return to item display
  const handleCancel = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Are you sure you want to close?')) {
      return
    }
    // Temporarily clear dirty so the X button interceptor doesn't double-prompt
    setIsDirty(false)
    const closeBtn = document.querySelector('.custom-modal-close-button')
    if (closeBtn) {
      setTimeout(() => closeBtn.click(), 0)
    }
  }

  // Handle continue to proof review - generates PDF and advances
  const handleContinueToProof = async () => {
    // Validate quantities first
    const totalQuantity = Object.values(quantities).reduce((sum, q) => sum + q, 0)
    if (totalQuantity === 0) {
      setErrors({ quantity: quantityOptions.length > 1 ? 'Please enter a quantity for at least one option.' : 'Please enter a quantity.' })
      return
    }

    // Validate that both sides have been viewed for multipage items
    if (isMultipage && !hasViewedBack) {
      setErrors({ viewBack: 'Please view the back of the card before continuing.' })
      return
    }

    setProofError(null)

    // Generate proof by capturing canvas screenshots for each page
    if (generateProofUrl) {
      setIsGeneratingProof(true)

      try {
        // Temporarily hide safety zone and frame edges for clean export
        var prevSafetyZone = showSafetyZone
        var prevHideFrameEdges = hideFrameEdges
        setShowSafetyZone(false)
        setHideFrameEdges(true)

        // Wait for state to apply
        await new Promise(function(r) { setTimeout(r, 200) })

        var exportFn = canvasExportFnRef.current
        var pageImages = []

        if (isMultipage) {
          // Export each page (export includes background + text composited)
          for (var pageIdx = 0; pageIdx < allPages.length; pageIdx++) {
            setCurrentPage(pageIdx)
            await new Promise(function(r) { setTimeout(r, 300) })
            if (exportFn) {
              var dataUrl = await exportFn()
              pageImages.push(dataUrl)
            }
          }
          // Restore to first page
          setCurrentPage(0)
        } else {
          // Single page
          if (exportFn) {
            pageImages.push(await exportFn())
          }
        }

        // Restore safety zone and frame edges
        setShowSafetyZone(prevSafetyZone)
        setHideFrameEdges(prevHideFrameEdges)

        if (pageImages.length === 0) {
          setProofError('Failed to generate proof images. Please try again.')
          return
        }

        // Send the same images to the server to render the actual printable
        // PDF. This is now AWAITED, not fire-and-forget: it used to be a
        // background fetch().catch() fired after already advancing to
        // proof_review, so a customer could reach "Add to Cart" before the
        // request finished (a multi-page poster can take minutes) or after
        // it failed outright -- either way with no visible error, leaving
        // the order with no printable PDF at all. Blocking here on success
        // means the PDF is guaranteed to exist by the time the customer can
        // continue, and a real failure is surfaced instead of swallowed.
        var templateData = getTemplateDataFromCanvas()
        var fieldState = getFullFieldState()
        var proofResponse = await fetch(generateProofUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          },
          body: JSON.stringify({
            template_data: templateData,
            field_state: fieldState,
            canvas_images: pageImages,
          }),
        })

        if (!proofResponse.ok) {
          var errorData = await proofResponse.json().catch(function() { return {} })
          setProofError(errorData.error || 'Failed to generate the printable proof. Please try again.')
          return
        }

        setProofPageImages(pageImages)
        setProofCurrentPage(0)
        setHasViewedProofFront(true)
        setHasViewedProofBack(pageImages.length <= 1)
        setCurrentStep('proof_review')
      } catch (error) {
        console.error('Error generating proof:', error)
        setProofError('An error occurred generating the proof. Please try again.')
      } finally {
        setIsGeneratingProof(false)
      }
    } else {
      // No proof URL configured, skip to proof review anyway
      setHasViewedProofFront(true)
      setHasViewedProofBack(true)
      setCurrentStep('proof_review')
    }
  }

  // Handle going back to edit from proof review
  const handleCorrect = () => {
    setProofPageImages([])
    setProofCurrentPage(0)
    setCurrentStep('template_edit')
  }

  // Ref for canvas panel to calculate available height
  const canvasPanelRef = useRef(null)
  const [canvasHeight, setCanvasHeight] = useState(null)

  // Calculate available height for the canvas panel
  useEffect(() => {
    if (currentStep !== 'template_edit') return

    const calculateHeight = () => {
      if (canvasPanelRef.current) {
        // Account for page tabs and padding
        const panelHeight = canvasPanelRef.current.clientHeight
        const tabsHeight = isMultipage ? 42 : 0
        setCanvasHeight(panelHeight - tabsHeight - 24) // 24px for padding
      }
    }

    // Delay to let the layout render first
    const timeout = setTimeout(calculateHeight, 150)
    window.addEventListener('resize', calculateHeight)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', calculateHeight)
    }
  }, [currentStep, isMultipage])

  // Render template editing step
  const renderTemplateEditor = () => (
    <>
      {isSubmitted ? (
        // Success state
        <Box className={styles.successContainer} textAlign="center" py={4}>
          <Typography variant="h5" style={{ color: 'green', marginBottom: '16px', fontSize: '22px' }}>
            {successMessage}
          </Typography>
          <Box display="flex" justifyContent="center" gap={2} mt={3}>
            <Button
              variant="outlined"
              onClick={handleContinueShopping}
              size="large"
              style={{ marginRight: '16px', fontSize: '16px', padding: '12px 24px' }}
            >
              Continue Shopping
            </Button>
            {checkoutUrl && (
              <Button
                variant="contained"
                color="primary"
                href={checkoutUrl}
                size="large"
                style={{ fontSize: '16px', padding: '12px 24px' }}
              >
                Checkout
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        // Side-by-side layout: canvas left, sidebar right
        <div className={styles.editorLayout}>
          {/* Left panel: Canvas */}
          <div className={styles.canvasPanel} ref={canvasPanelRef}>
            <div className={styles.canvasPanelInner}>
              {/* Page tabs for multipage templates */}
              {isMultipage && (
                <div className={styles.pageTabs}>
                  {allPages.map((page, idx) => {
                    var isActive = currentPage === idx
                    return (
                      <Button
                        key={page.id || 'page1'}
                        variant={isActive ? 'contained' : 'outlined'}
                        color={isActive ? 'primary' : 'default'}
                        size="small"
                        onClick={() => {
                          setCurrentPage(idx)
                          if (idx === 1) {
                            setHasViewedBack(true)
                            setErrors(prev => {
                              const { viewBack, ...rest } = prev
                              return rest
                            })
                          }
                        }}
                        style={{
                          minWidth: 100,
                          fontWeight: isActive ? 700 : 400,
                          borderBottom: isActive ? '3px solid #008fcf' : '3px solid transparent',
                          borderRadius: isActive ? '4px 4px 0 0' : undefined,
                        }}
                      >
                        <span style={{ marginRight: 6, fontSize: 16 }}>{idx === 0 ? '\u25A3' : '\u25A2'}</span>
                        {idx === 0 ? 'Front' : 'Back'}
                      </Button>
                    )
                  })}
                </div>
              )}

              {/* KonvaCanvas - auto-fits to both width and height */}
              <KonvaCanvas
                fields={visibleCanvasFields}
                setFields={setCanvasFields}
                imageUrl={currentImageUrl}
                template={template}
                fonts={fonts}
                hideBoundingBoxes={hideFrameEdges}
                onHideBoundingBoxesChange={setHideFrameEdges}
                showControls={false}
                showFixedToolbar={true}
                showZoomControls={true}
                onOverflowChange={setHasOverflow}
                showSafetyZone={showSafetyZone}
                onSafetyZoneChange={setShowSafetyZone}
                maxContainerHeight={canvasHeight}
                onSpellCheck={() => setShowSpellCheck(true)}
                onAddText={handleAddText}
                onExportReady={(fn) => { canvasExportFnRef.current = fn }}
              />
            </div>
          </div>

          {/* Right sidebar */}
          <div className={styles.sidebar}>
            {/* Item info */}
            <div className={styles.sidebarSection}>
              <h2 className={styles.sidebarTitle}>{itemName}</h2>
              <p className={styles.sidebarSubtitle}>
                {itemNumber && <>Item #{itemNumber}</>}
                {itemNumber && currentSchoolName && <> &middot; </>}
                {currentSchoolName}
              </p>
            </div>

            {/* Instructions */}
            <div className={styles.sidebarSection}>
              <div className={styles.sidebarSectionHeading}>How to Customize</div>
              <ol className={styles.instructionsList}>
                <li className={styles.instructionStep}>Click any text to select it, then use the toolbar to change font, size, and color</li>
                <li className={styles.instructionStep}>Double-click text to edit the wording</li>
                <li className={styles.instructionStep}>Right-click to copy, paste, or delete elements</li>
              </ol>
              <label className={styles.hideEdgesToggle}>
                <Checkbox
                  checked={hideFrameEdges}
                  onChange={(e) => setHideFrameEdges(e.target.checked)}
                  size="small"
                  color="primary"
                  style={{ padding: '2px 8px 2px 0' }}
                />
                Hide text box outlines
              </label>
              <label className={styles.hideEdgesToggle}>
                <Checkbox
                  checked={showSafetyZone}
                  onChange={(e) => setShowSafetyZone(e.target.checked)}
                  size="small"
                  color="primary"
                  style={{ padding: '2px 8px 2px 0' }}
                />
                Show safe print area
              </label>
              {showSafetyZone && (
                <div className={styles.marginHint}>
                  <span className={styles.marginHintLine} />
                  Keep all elements within the dotted line. Content beyond this boundary may be trimmed after printing.
                </div>
              )}
            </div>

            {/* Quantities */}
            <div className={styles.sidebarSection}>
              <div className={styles.sidebarSectionHeading} style={{ textAlign: 'center' }}>
                Quantity <span style={{ color: 'crimson' }}>*</span>
              </div>
              <div className={styles.quantitySection}>
                {quantityOptions.map(opt => (
                  opt.inputType === 'select' ? (
                    <FormControl key={opt.name} variant="outlined" size="small" fullWidth>
                      <InputLabel style={{ fontSize: '14px' }}>{opt.label}</InputLabel>
                      <MuiSelect
                        value={quantities[opt.name]}
                        onChange={(e) => handleQuantityChange(opt.name, e.target.value)}
                        label={opt.label}
                        style={{ fontSize: '14px', background: 'white' }}
                        error={!!errors['quantity']}
                      >
                        {(opt.options || []).map(val => (
                          <MenuItem key={val} value={val} style={{ fontSize: '14px' }}>
                            {val}
                          </MenuItem>
                        ))}
                      </MuiSelect>
                    </FormControl>
                  ) : (
                    <div className={styles.quantityRow} key={opt.name}>
                      {quantityOptions.length > 1 && (
                        <span className={styles.quantityLabel}>{opt.label}</span>
                      )}
                      <TextField
                        type="number"
                        variant="outlined"
                        size="small"
                        value={quantities[opt.name]}
                        onChange={(e) => handleQuantityChange(opt.name, e.target.value)}
                        inputProps={{ min: 0, step: opt.step || 1, style: { fontSize: '16px', fontWeight: 600, textAlign: 'center' } }}
                        error={!!errors['quantity']}
                        className={styles.quantityInput}
                      />
                    </div>
                  )
                ))}
              </div>
              {errors['quantity'] && (
                <Typography variant="body2" color="error" style={{ fontSize: '13px', marginTop: '8px' }}>
                  {errors['quantity']}
                </Typography>
              )}
              {proofError && (
                <Typography variant="body2" color="error" style={{ fontSize: '13px', marginTop: '8px' }}>
                  {proofError}
                </Typography>
              )}
            </div>

            {/* Action buttons - pushed to bottom */}
            <div className={styles.sidebarActions}>
              {errors['viewBack'] && (
                <Typography
                  variant="body2"
                  style={{ textAlign: 'center', color: '#c62828', fontSize: '13px', marginBottom: '8px' }}
                >
                  {errors['viewBack']}
                </Typography>
              )}
              <Button
                variant="contained"
                fullWidth
                onClick={handleContinueToProof}
                disabled={isGeneratingProof}
                style={{
                  fontSize: '15px',
                  padding: '10px 16px',
                  textTransform: 'uppercase',
                  backgroundColor: '#008fcf',
                  color: '#fff',
                  fontWeight: 600,
                }}
              >
                {isGeneratingProof ? 'Generating...' : 'Next: Review Proof'}
              </Button>
              <Button
                variant="outlined"
                color="default"
                fullWidth
                onClick={handleCancel}
                style={{ fontSize: '14px', padding: '8px 16px', textTransform: 'uppercase' }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )

  // Render proof review step
  const renderProofReview = () => (
    <>
      {/* Header */}
      <div style={{ marginBottom: 8, textAlign: 'center' }}>
        {!isSubmitted && (
          <Typography variant="h5" style={{ color: '#008fcf', fontSize: '22px', fontWeight: 700 }}>
            Review Your Proof
          </Typography>
        )}
      </div>

      {isSubmitted ? (
        // Success state
        <Box className={styles.successContainer} textAlign="center" py={4}>
          <Typography variant="h5" style={{ color: 'green', marginBottom: '16px', fontSize: '22px' }}>
            {successMessage}
          </Typography>
          <Box display="flex" justifyContent="center" gap={2} mt={3}>
            <Button
              variant="outlined"
              onClick={handleContinueShopping}
              size="large"
              style={{ marginRight: '16px', fontSize: '16px', padding: '12px 24px' }}
            >
              Continue Shopping
            </Button>
            {checkoutUrl && (
              <Button
                variant="contained"
                color="primary"
                href={checkoutUrl}
                size="large"
                style={{ fontSize: '16px', padding: '12px 24px' }}
              >
                Checkout
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        <Box>
          {/* Proof Preview */}
          <Paper elevation={1} style={{ padding: '12px', marginBottom: '10px', textAlign: 'center' }}>
            {proofPageImages.length > 0 ? (
              <>
                {proofPageImages.length > 1 && (
                  <div className={styles.pageTabs} style={{ justifyContent: 'center', marginBottom: 8 }}>
                    {proofPageImages.map((_, idx) => {
                      var isActive = proofCurrentPage === idx
                      var viewed = idx === 0 ? hasViewedProofFront : hasViewedProofBack
                      return (
                        <Button
                          key={idx}
                          variant={isActive ? 'contained' : 'outlined'}
                          color={isActive ? 'primary' : 'default'}
                          size="small"
                          onClick={() => {
                            setProofCurrentPage(idx)
                            if (idx === 0) setHasViewedProofFront(true)
                            if (idx === 1) setHasViewedProofBack(true)
                          }}
                          style={{
                            minWidth: 100,
                            fontWeight: isActive ? 700 : 400,
                            borderBottom: isActive ? '3px solid #008fcf' : '3px solid transparent',
                            borderRadius: isActive ? '4px 4px 0 0' : undefined,
                          }}
                        >
                          <span style={{ marginRight: 6, fontSize: 16 }}>{idx === 0 ? '\u25A3' : '\u25A2'}</span>
                          {idx === 0 ? 'Front' : 'Back'}{viewed ? ' \u2713' : ''}
                        </Button>
                      )
                    })}
                  </div>
                )}
                <Box style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  overflow: 'auto',
                  marginBottom: '8px',
                  background: '#f5f5f5',
                  height: '50vh',
                  display: 'flex',
                  alignItems: proofZoom <= 100 ? 'center' : 'flex-start',
                  justifyContent: proofZoom <= 100 ? 'center' : 'flex-start',
                }}>
                  <img
                    src={proofPageImages[proofCurrentPage]}
                    alt={`Proof page ${proofCurrentPage + 1}`}
                    style={{
                      display: 'block',
                      margin: proofZoom <= 100 ? '0 auto' : '0',
                      maxHeight: proofZoom <= 100 ? '100%' : 'none',
                      maxWidth: proofZoom <= 100 ? '100%' : 'none',
                      width: proofZoom > 100 ? proofZoom + '%' : 'auto',
                      objectFit: 'contain',
                      transform: proofZoom < 100 ? 'scale(' + (proofZoom / 100) + ')' : 'none',
                      transformOrigin: 'center center',
                    }}
                  />
                </Box>
                {/* Zoom controls */}
                <Box display="flex" justifyContent="center" alignItems="center" style={{ gap: 8, marginBottom: 4 }}>
                  <Button size="small" variant="outlined" style={{ minWidth: 32, padding: '2px 6px' }}
                    onClick={() => setProofZoom(function(z) { return Math.max(25, z - 5) })}>
                    &minus;
                  </Button>
                  <Typography variant="body2" style={{ fontSize: 13, minWidth: 40, textAlign: 'center' }}>
                    {proofZoom}%
                  </Typography>
                  <Button size="small" variant="outlined" style={{ minWidth: 32, padding: '2px 6px' }}
                    onClick={() => setProofZoom(function(z) { return Math.min(200, z + 5) })}>
                    +
                  </Button>
                  <Button size="small" variant="outlined" style={{ minWidth: 32, padding: '2px 6px' }}
                    onClick={() => setProofZoom(100)}>
                    Fit
                  </Button>
                </Box>
              </>
            ) : proofPdfUrl ? (
              <>
                <Box style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '20px',
                  background: '#f5f5f5'
                }}>
                  <object
                    data={`${proofPdfUrl}#view=Fit&toolbar=0&navpanes=0`}
                    type="application/pdf"
                    style={{ width: '100%', height: '80vh', border: 'none' }}
                  >
                    <Box style={{ padding: '40px', textAlign: 'center' }}>
                      <Typography variant="body1" style={{ marginBottom: '16px' }}>
                        PDF preview requires a PDF viewer. Click below to view your proof.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        href={proofPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View PDF Proof
                      </Button>
                    </Box>
                  </object>
                </Box>
              </>
            ) : (
              <Typography variant="body1" style={{ fontSize: '16px', color: '#666' }}>
                Proof preview not available. Please verify your design before adding to cart.
              </Typography>
            )}
          </Paper>

          {/* Approval checkbox */}
          <Paper elevation={1} style={{ padding: '12px 16px', marginBottom: '10px' }}>
            <FormControlLabel
              style={{ marginLeft: 0, gap: '4px', alignItems: 'flex-start' }}
              control={
                <Checkbox
                  checked={approved}
                  onChange={(e) => {
                    setApproved(e.target.checked)
                    if (e.target.checked && errors['approval']) {
                      setErrors(prev => {
                        const newErrors = { ...prev }
                        delete newErrors['approval']
                        return newErrors
                      })
                    }
                  }}
                  color="primary"
                  style={{ transform: 'scale(1.4)', padding: '8px' }}
                />
              }
              label={
                <Typography variant="body1" style={{ fontSize: '16px', fontWeight: 'bold', color: '#c62828' }}>
                  By checking this box, I approve all info is correct and free of errors.
                  Items will be printed with the exact wording provided.
                  <span style={{ fontStyle: 'italic', marginLeft: '4px' }}>
                    * Required
                  </span>
                </Typography>
              }
            />
            {errors['approval'] && (
              <Typography variant="body2" color="error" display="block" style={{ marginTop: '8px', fontSize: '14px' }}>
                {errors['approval']}
              </Typography>
            )}
          </Paper>

          {/* Correct and Add to Cart buttons */}
          {proofPageImages.length > 1 && (!hasViewedProofFront || !hasViewedProofBack) && (
            <Typography
              variant="body2"
              style={{ textAlign: 'center', color: '#c62828', fontSize: '14px', marginBottom: '8px' }}
            >
              Please review both the front and back before approving.
            </Typography>
          )}
          <Box display="flex" justifyContent="center" style={{ gap: 16, marginTop: 10 }}>
            <Button
              variant="outlined"
              color="default"
              size="large"
              onClick={handleCorrect}
              style={{ minWidth: '160px', fontSize: '15px', padding: '10px 24px' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 8 }}>
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
              Go Back and Edit
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={isSubmitting || (proofPageImages.length > 1 && (!hasViewedProofFront || !hasViewedProofBack))}
              style={{ minWidth: '180px', fontSize: '15px', padding: '10px 24px' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 8 }}>
                <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM7.17 14.75l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1 1 0 0020 4H5.21l-.94-2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7.42c-.14 0-.25-.11-.25-.25z"/>
              </svg>
              {isSubmitting ? 'Adding...' : 'Add to Cart'}
            </Button>
          </Box>
        </Box>
      )}
    </>
  )

  // Handle spell check field update
  const handleSpellCheckFieldUpdate = useCallback((fieldId, text) => {
    setCanvasFields(prev => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], text, sampleText: null }
    }))
  }, [])

  return (
    <ThemeProvider theme={brandTheme}>
      <div className={styles.container}>
        {isGeneratingProof && (
          <div className={styles.loadingOverlay}>
            <CircularProgress size={48} style={{ color: '#008fcf' }} />
            <Typography variant="body1" style={{ marginTop: 16, fontSize: 16, color: '#333' }}>
              Generating your proof... this can take a minute or two for multi-page designs.
            </Typography>
          </div>
        )}
        {currentStep === 'school_info' && renderSchoolSelection()}
        {currentStep === 'template_edit' && renderTemplateEditor()}
        {currentStep === 'proof_review' && renderProofReview()}

        {/* Spell check panel */}
        {showSpellCheck && (
          <SpellCheckPanel
            fields={canvasFields}
            onFieldUpdate={handleSpellCheckFieldUpdate}
            onClose={() => setShowSpellCheck(false)}
          />
        )}
      </div>
    </ThemeProvider>
  )
}
